import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { GridFooter } from '@mui/x-data-grid';
import format from 'string-template';

import EmptyView from '../../../../components/EmptyView';
import QuickSearchToolbar from '../../../../components/QuickSearchToolbar';
import useToast from '../../../../hooks/useToast';
import {
  SUSPECT_THRESHOLD_DAYS,
  TaskCellState,
  buildACLeadRows,
  buildACSlackMessage,
  getSlackDirectMessageUrl,
  sortACSummaries,
} from '../../utils/acLeadUtils';

import getColumns, { COLUMN_GROUPING_MODEL } from './columns';
import {
  ACLeadGrid,
  FooterRow,
  Legend,
  LegendDot,
  LegendItem,
  LegendSwatch,
} from './styles';
import texts from './texts.json';

/** Column grouping is still behind a flag in this version of the grid. */
const EXPERIMENTAL_FEATURES = { columnGrouping: true };

/** Matches the design's row height: seven single-number cells need no more room than one line. */
const ROW_HEIGHT = 46;

/**
 * What every marker in the matrix means.
 *
 * The states are told apart by colour and weight, so the ones a lead has to act on differently —
 * a breach from this week and a five-year-old suspect item — need saying out loud once.
 */
const LEGEND_ITEMS = [
  { state: TaskCellState.CRITICAL, label: texts.legend.critical, isDot: true },
  { state: TaskCellState.BREACHED, label: texts.legend.breached, isDot: true },
  {
    state: TaskCellState.SUSPECT,
    label: format(texts.legend.suspect, { days: SUSPECT_THRESHOLD_DAYS }),
    isDot: true,
  },
  { state: TaskCellState.WITHIN_SLA, label: texts.legend.withinSla, swatch: '0' },
  { state: TaskCellState.UNKNOWN_AGE, label: texts.legend.unknownAge, swatch: texts.unknownCell },
];

const NoRowsOverlay = ({ hasFilter }) => (
  <EmptyView content={hasFilter ? texts.noTasksForSelection : texts.allClear} />
);

NoRowsOverlay.propTypes = { hasFilter: PropTypes.bool };
NoRowsOverlay.defaultProps = { hasFilter: false };

/** The grid's own footer, with the matrix legend alongside it rather than above the table. */
const Footer = () => (
  <FooterRow>
    <Legend>
      {LEGEND_ITEMS.map(({
        state, label, isDot, swatch,
      }) => (
        <LegendItem key={state}>
          {isDot ? <LegendDot $state={state} /> : <LegendSwatch $state={state}>{swatch}</LegendSwatch>}
          {label}
        </LegendItem>
      ))}
    </Legend>
    <GridFooter />
  </FooterRow>
);

/**
 * The roster: one row per AC, with their coach breakdown underneath when expanded.
 *
 * Owns which ACs are open and the chase action, since neither means anything outside the table.
 * The summaries are handed in because the stats above the table are counted from the same set.
 */
const ACLeadTable = ({
  summaries,
  isLoading,
  hasFilter,
  toolbarProps,
  filterTools,
}) => {
  const [expandedACIds, setExpandedACIds] = useState(() => new Set());
  // Empty means the roster's own order: most past due first, ties broken by the longest wait.
  const [sortModel, setSortModel] = useState([]);
  const { showToast } = useToast();

  const handleToggleAC = useCallback((acId) => {
    setExpandedACIds((expanded) => {
      const next = new Set(expanded);

      if (!next.delete(acId)) {
        next.add(acId);
      }

      return next;
    });
  }, []);

  const handleSlackClick = useCallback((summary) => {
    /*
     * Opened before the clipboard is written, not after. Safari only lets window.open through the
     * popup blocker while the click that triggered it is still on the stack, and awaiting the
     * clipboard promise first makes that gesture stale — the DM would then be blocked silently.
     */
    const slackWindow = window.open(getSlackDirectMessageUrl(summary.acSlackId), '_blank');

    if (slackWindow) {
      // Severs the opener handle without passing 'noopener', which would return null here and
      // leave a blocked popup indistinguishable from an opened one.
      slackWindow.opener = null;
    }

    // Slack cannot prefill a direct message from a link, so the rundown goes to the clipboard and
    // the lead pastes it into the conversation opened above.
    navigator.clipboard.writeText(buildACSlackMessage(summary))
      .then(() => showToast(format(texts.chase.messageCopied, { acName: summary.acName })))
      .catch(() => showToast(texts.chase.messageCopyFailed, { error: true }));

    if (!slackWindow) {
      showToast(format(texts.chase.popupBlocked, { acName: summary.acName }), { error: true });
    }
  }, [showToast]);

  /*
   * Sorted as summaries, then re-flattened, so a coach row stays under its own AC. Letting the grid
   * sort the flat rows would scatter them, which is why `sortingMode` is handed over below rather
   * than the columns being left unsortable.
   */
  const rows = useMemo(
    () => buildACLeadRows(sortACSummaries(summaries, sortModel), expandedACIds),
    [summaries, sortModel, expandedACIds],
  );

  const columns = useMemo(
    () => getColumns({ expandedACIds, onToggleAC: handleToggleAC, onSlackClick: handleSlackClick }),
    [expandedACIds, handleToggleAC, handleSlackClick],
  );

  return (
    <ACLeadGrid
      rows={rows}
      columns={columns}
      columnGroupingModel={COLUMN_GROUPING_MODEL}
      experimentalFeatures={EXPERIMENTAL_FEATURES}
      rowHeight={ROW_HEIGHT}
      loading={isLoading}
      /*
       * "server" only means the grid renders the header controls without reordering rows itself —
       * `sortACSummaries` does the ordering in memory. Nothing is refetched.
       */
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      components={{ Toolbar: QuickSearchToolbar, NoRowsOverlay, Footer }}
      componentsProps={{
        toolbar: { ...toolbarProps, placeholder: texts.toolbarPlaceholder, filterTools },
        noRowsOverlay: { hasFilter },
      }}
    />
  );
};

ACLeadTable.propTypes = {
  summaries: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  hasFilter: PropTypes.bool,
  toolbarProps: PropTypes.object,
  filterTools: PropTypes.array,
};

ACLeadTable.defaultProps = {
  isLoading: false,
  hasFilter: false,
  toolbarProps: {},
  filterTools: [],
};

export default ACLeadTable;
